/**
 * Revenue calculation functions for broiler production
 */

/**
 * Calculate meat revenue from total weight and price per kg
 * 
 * @param totalWeight - Total live weight in kg
 * @param pricePerKg - Meat price per kilogram
 * @returns Total meat revenue
 */
export function calculateMeatRevenue(totalWeight: number, pricePerKg: number): number {
  return totalWeight * pricePerKg;
}

/**
 * Calculate feed bonus or penalty based on FCR performance
 * If actual FCR is better (lower) than target, apply bonus rate
 * If actual FCR is worse (higher) than target, apply penalty rate
 * 
 * @param targetFCR - Target FCR value
 * @param actualFCR - Actual achieved FCR
 * @param totalWeight - Total live weight in kg
 * @param bonusRate - Bonus rate per kg for positive performance
 * @param penaltyRate - Penalty rate per kg for negative performance
 * @returns Feed bonus (positive) or penalty (negative) amount
 */
export function calculateFeedBonus(
  targetFCR: number,
  actualFCR: number,
  totalWeight: number,
  bonusRate: number,
  penaltyRate: number
): number {
  const fcrDifference = targetFCR - actualFCR;
  const savedKg = fcrDifference * totalWeight;
  
  if (savedKg >= 0) {
    // Better performance - apply bonus
    return savedKg * bonusRate;
  } else {
    // Worse performance - apply penalty
    return savedKg * penaltyRate;
  }
}

/**
 * Calculate chick bonus or penalty based on mortality performance
 * If actual mortality is below limit, apply bonus
 * If actual mortality is above limit, apply penalty
 * 
 * @param initialCount - Initial chick placement count
 * @param totalCount - Total harvested count
 * @param mortalityLimit - Allowed mortality percentage
 * @param bonusRate - Bonus rate per bird for good performance
 * @param penaltyRate - Penalty rate per bird for poor performance
 * @returns Chick bonus (positive) or penalty (negative) amount
 */
export function calculateChickBonus(
  initialCount: number,
  totalCount: number,
  mortalityLimit: number,
  bonusRate: number,
  penaltyRate: number
): number {
  const allowedDeaths = initialCount * (mortalityLimit / 100);
  const actualDeaths = initialCount - totalCount;
  const difference = allowedDeaths - actualDeaths;
  
  if (difference >= 0) {
    // Better than limit - apply bonus
    return difference * bonusRate;
  } else {
    // Worse than limit - apply penalty
    return difference * penaltyRate;
  }
}

/**
 * Calculate EPEF premium based on achieved EPEF tier
 * Higher EPEF values earn higher premiums
 * 
 * @param epef - Achieved EPEF value
 * @param limits - Array of three EPEF thresholds [limit1, limit2, limit3]
 * @param prices - Array of three premium prices [price1, price2, price3]
 * @param totalWeight - Total live weight in kg
 * @returns EPEF premium amount
 */
export function calculateEPEFPremium(
  epef: number,
  limits: number[],
  prices: number[],
  totalWeight: number
): number {
  let premiumRate = 0;
  
  if (epef > limits[2]) {
    premiumRate = prices[2];
  } else if (epef > limits[1]) {
    premiumRate = prices[1];
  } else if (epef > limits[0]) {
    premiumRate = prices[0];
  }
  
  return premiumRate * totalWeight;
}

/**
 * Calculate coal support subsidy
 * 
 * @param totalWeight - Total live weight in kg
 * @param supportRate - Support rate per kg (already in TRY, e.g., 0.75 for 75 kuruş)
 * @returns Coal support amount
 */
export function calculateCoalSupport(totalWeight: number, supportRate: number): number {
  return supportRate * totalWeight;
}
